import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/shared/ui/Modal/Modal';
import { Loader } from '@/shared/ui/Loader/Loader';
import { StatusDot } from '@/shared/ui/StatusDot';
import { DateInput } from '@/shared/ui/DateInput/DateInput';
import { Button } from '@/shared/ui/Button/Button';
import { formatDateTimeForDisplay } from '@/shared/lib/utils/dateFormat';
import { compareMaskedDates, maskToIsoDate } from '@/shared/lib/utils/dateRange';
import { formatPrivateId } from '@/shared/lib/utils/formatPrivateId';
import { useGetAllServersHistoryQuery } from '@/entities/User';
import {
  useGetClubServersHistoryQuery,
  type ClubServerHistoryItem,
  type ClubServersHistoryResponse,
} from '@/entities/Club';
import type { AllServerHistoryItem, AllServersHistoryResponse } from '@/entities/User/api/userApi';
import s from './ServersHistoryModal.module.scss';

interface BaseProps {
  onClose: () => void;
}

interface AllModeProps extends BaseProps {
  mode: 'all';
}

interface ClubModeProps extends BaseProps {
  mode: 'club';
  clubId: number;
  clubName: string;
}

type Props = AllModeProps | ClubModeProps;

const PAGE_SIZE = 20;

const formatDuration = (seconds: number | null | undefined): string => {
  if (!seconds || seconds <= 0) {
    return '0м';
  }
  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours}ч ${minutes}м`;
  }
  if (hours > 0) {
    return `${hours}ч`;
  }
  return `${minutes}м`;
};

const formatTotalDuration = (seconds: number | null | undefined): string => {
  if (!seconds || seconds <= 0) {
    return '0м';
  }
  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours}ч ${minutes}м`;
  }
  if (hours > 0) {
    return `${hours}ч`;
  }
  return `${minutes}м`;
};

const getTimezone = (): string => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || 'UTC';
  } catch (e) {
    return 'UTC';
  }
};

export const ServersHistoryModal: React.FC<Props> = (props) => {
  const { mode, onClose } = props;

  const [startAtMask, setStartAtMask] = useState('');
  const [endAtMask, setEndAtMask] = useState('');
  const [startError, setStartError] = useState('');
  const [endError, setEndError] = useState('');
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<(AllServerHistoryItem | ClubServerHistoryItem)[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [timezone] = useState<string>(getTimezone);

  const startAt = maskToIsoDate(startAtMask);
  const endAt = maskToIsoDate(endAtMask);
  const hasRangeError = Boolean(startError || endError);

  const filterKey = useMemo(() => {
    const clubKey = mode === 'club' ? String(props.clubId) : 'all';
    return `${mode}-${clubKey}-${startAt ?? ''}-${endAt ?? ''}-${timezone}`;
  }, [mode, 'clubId' in props ? props.clubId : undefined, startAt, endAt, timezone]);

  useEffect(() => {
    setOffset(0);
    setItems([]);
  }, [filterKey]);

  const isAllMode = mode === 'all';
  const isClubMode = mode === 'club';

  const commonArgs = {
    startAt,
    endAt,
    timezone,
    limit: PAGE_SIZE,
    offset,
  };

  const {
    data: allListData,
    isLoading: allListLoading,
    error: allListError,
    isFetching: allListFetching,
  } = useGetAllServersHistoryQuery(commonArgs, { skip: !isAllMode || hasRangeError });

  const {
    data: clubData,
    isLoading: clubLoading,
    error: clubError,
    isFetching: clubFetching,
  } = useGetClubServersHistoryQuery(
    {
      clubId: isClubMode ? props.clubId : 0,
      startAt,
      endAt,
      timezone,
      limit: PAGE_SIZE,
      offset,
    },
    { skip: !isClubMode || hasRangeError },
  );

  let pageItems: (AllServerHistoryItem | ClubServerHistoryItem)[] = [];
  let isLoading = false;
  let error: unknown = null;
  let isFetching = false;
  let totalSecondsFromBackend = 0;

  if (isAllMode) {
    const resp = (allListData ?? { items: [], totalSeconds: 0 }) as AllServersHistoryResponse;
    pageItems = resp.items ?? [];
    isLoading = allListLoading;
    error = allListError as unknown;
    totalSecondsFromBackend = resp.totalSeconds ?? 0;
    isFetching = allListFetching;
  } else {
    const resp = (clubData ?? { items: [], totalSeconds: 0 }) as ClubServersHistoryResponse;
    pageItems = resp.items ?? [];
    isLoading = clubLoading;
    error = clubError as unknown;
    totalSecondsFromBackend = resp.totalSeconds ?? 0;
    isFetching = clubFetching;
  }

  useEffect(() => {
    setItems((prev) => {
      if (!pageItems || pageItems.length === 0) {
        if (offset === 0) {
          return [];
        }
        return prev;
      }

      if (offset === 0) {
        return pageItems;
      }

      const existingKeys = new Set(prev.map((i) => `${i.privateServerId ?? ''}-${i.createdAt ?? ''}`));
      const next = pageItems.filter((i) => !existingKeys.has(`${i.privateServerId ?? ''}-${i.createdAt ?? ''}`));
      return [...prev, ...next];
    });
    setHasMore(pageItems.length === PAGE_SIZE);
  }, [pageItems, offset]);

  const totalSecondsForDisplay = items.length === 0 ? 0 : totalSecondsFromBackend;

  const onLoadMore = () => {
    if (!isFetching && hasMore) {
      setOffset((prev) => prev + PAGE_SIZE);
    }
  };

  const title = mode === 'all' ? 'История серверов' : `История серверов клуба: ${props.clubName}`;

  const onChangeStart = (value: React.ChangeEvent<HTMLInputElement> | string) => {
    const next = typeof value === 'string' ? value : value.target.value;
    setStartAtMask(next);

    const cmp = compareMaskedDates(next, endAtMask);

    if (cmp === null) {
      setStartError('');
      setEndError('');
      return;
    }

    if (cmp === 1) {
      setStartError('Дата начала не может быть позже даты окончания');
      setEndError('');
    } else {
      setStartError('');
      setEndError('');
    }
  };

  const onChangeEnd = (value: React.ChangeEvent<HTMLInputElement> | string) => {
    const next = typeof value === 'string' ? value : value.target.value;
    setEndAtMask(next);

    const cmp = compareMaskedDates(startAtMask, next);

    if (cmp === null) {
      setStartError('');
      setEndError('');
      return;
    }

    if (cmp === 1) {
      setEndError('Дата окончания не может быть раньше даты начала');
      setStartError('');
    } else {
      setStartError('');
      setEndError('');
    }
  };

  return (
    <Modal open onClose={onClose} title={title}>
      <div className={s.root}>
        <div className={s.filtersRow}>
          <div className={s.period}>
            <div className={s.periodField}>
              <span className={s.periodLabel}>Начало</span>
              <DateInput
                value={startAtMask}
                onChange={onChangeStart}
                placeholder="__.__.____"
                error={startError}
                name="servers-history-start"
              />
            </div>
            <div className={s.periodField}>
              <span className={s.periodLabel}>Окончание</span>
              <DateInput
                value={endAtMask}
                onChange={onChangeEnd}
                placeholder="__.__.____"
                error={endError}
                name="servers-history-end"
              />
            </div>
          </div>
          <div className={s.totalTime}>Время на серверах за период: {formatTotalDuration(totalSecondsForDisplay)}</div>
        </div>

        <div className={s.container}>
          {isLoading && items.length === 0 && (
            <div className={s.loaderWrapper}>
              <Loader />
            </div>
          )}
          {error && !isLoading && <div className={s.emptyWrapper}>Ошибка загрузки истории серверов</div>}
          {!isLoading && !error && items.length === 0 && <div className={s.emptyWrapper}>Серверов не найдено</div>}
          {!isLoading && !error && items.length > 0 && (
            <ul className={s.list}>
              {items.map((item, idx) => (
                <li key={`${item.privateServerId}-${idx}`} className={s.item}>
                  <div className={s.colLeft}>
                    <div className={s.titleRow}>
                      <StatusDot active={item.closedAt === null && item.isActive} size={8} />
                      <span className={s.primary}>{item.name || '—'}</span>
                    </div>
                    {'creatorLogin' in item && item.creatorLogin && (
                      <span className={s.primary}>{item.creatorLogin}</span>
                    )}
                    <span className={s.secondary}>{formatDateTimeForDisplay(item.createdAt)}</span>
                    <span className={s.secondary}>{formatDuration(item.totalTimeSeconds)}</span>
                  </div>
                  <div className={s.colRight}>
                    <span className={s.rightPrimary} title={item.privateServerId ?? undefined}>
                      {formatPrivateId(item.privateServerId)}
                    </span>
                    <span className={s.rightInfo}>Максимум игроков: {item.maxPlayers ?? '—'}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {hasMore && !isLoading && !error && (
            <Button onClick={onLoadMore} disabled={isFetching} className={s.loadMoreBtn}>
              {isFetching ? 'Загрузка...' : 'Показать ещё'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
